import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import TableCell from '@mui/material/TableCell';

import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import { Link } from 'react-router-dom';
import useNotification from '../../../../components/_hook/useNotification';
import { useUnpublishProductsMutation } from '../../../../../graphql/mutation/product/unpublishProductsMutation';
import StatusIconButton from '../../../../components/_iconButton/StatusIconButton';
import { usePublishProductsMutation } from '../../../../../graphql/mutation/product/publishProductsMutation';
import useLanguage from '../../../../components/_hook/useLanguage';
import Moment from '../../../../components/Moment';
import BaseImage from '../../../../components/_image/BaseImage';
import { IProductSchema } from '../../../../../graphql/type/product';
import CopyIconButton from '../../../../components/_iconButton/CopyIconButton';
import useCurrency from '../../../../components/_hook/useCurrency';
import ButtonAllowDropshipping from '../../_module/ButtonAllowDropshipping';
import ButtonDisallowDropshipping from '../../_module/ButtonDisallowDropshipping';
import Context from '../../../../context';
import useRoutes from '../../../../components/_hook/useRoutes';

interface IProps {
  item: IProductSchema;
  setTableRefreshKey?: (tableRefreshKey: number) => void;
}

export default function DropshippingProductTableItem(props: IProps) {
  const { t } = useTranslation();
  const { item, setTableRefreshKey } = props;
  const { path } = useRoutes();
  const { notification } = useNotification();
  const { getText } = useLanguage();
  const { company } = useContext(Context);
  const { currency, exchange } = useCurrency();
  const [
    publishProductsMutation,
    { loading: isPublishingProducts },
  ] = usePublishProductsMutation(
    `updatedProducts {
				_id
				status
			}`,
    {
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );
  const [
    unpublishProductsMutation,
    { loading: isUnpublishingProducts },
  ] = useUnpublishProductsMutation(
    `updatedProducts {
				_id
				status
			}`,
    {
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );

  async function onClickPublish() {
    if (item._id) {
      await publishProductsMutation({ variables: { _ids: [item._id] } });
    }
  }

  async function onClickUnpublish() {
    if (item._id) {
      await unpublishProductsMutation({ variables: { _ids: [item._id] } });
    }
  }

  const images = item.media && item.media.images ? item.media.images : [];
  const image = images.find((tempImage) => tempImage.status);

  return (
    <>
      <TableCell align="left">
        <Tooltip title={item._id}>
          <Button
            color="primary"
            component={Link}
            to={path('product.information', {
              companyID: item.company,
              docID: item._id,
            })}
          >
            {item._id.substr(0, 6)}
          </Button>
        </Tooltip>
        <CopyIconButton iconHtmlFontSize="0.8rem" copyText={item._id} />
      </TableCell>
      <TableCell align="left">{getText(item.name)}</TableCell>
      <TableCell align="left">
        {image ? (
          <BaseImage
            src={image.filenameSmallUrl || image.filenameUrl}
            style={{
              maxWidth: '100px',
              height: '35px',
            }}
            lightboxImages={images.map((tempImage) => tempImage.filenameUrl)}
            title={getText(item.name)}
            alt={getText(item.name)}
          />
        ) : (
          <span>{`${t('no')} ${t('image')}`}</span>
        )}
      </TableCell>
      <TableCell align="left">
        {item.prices && item.prices.final ? (
          <>
            {exchange(
              parseFloat((item.prices.final as unknown) as string),
              String(item.prices.currency),
              String(currency)
            )}
          </>
        ) : (
          <span
            style={{ color: 'red  ' }}
            data-toggle="tooltip"
            title={t('request_for_quote')}
          >
            {t('rfq')}
          </span>
        )}
      </TableCell>
      <TableCell align="left">
        {item.created && item.created.time && (
          <Moment time={item.created.time} />
        )}
      </TableCell>
      <TableCell align="right">
        {company.settings && company.settings.enableDropshipping && (
          <>
            {item.dropshipping && item.dropshipping.enable ? (
              <ButtonDisallowDropshipping products={[item]} iconButton />
            ) : (
              <ButtonAllowDropshipping products={[item]} iconButton />
            )}
          </>
        )}
        <StatusIconButton
          status={item.status}
          loading={isUnpublishingProducts || isPublishingProducts}
          tooltip={item.status ? t('publish') : t('unpublish')}
          onClick={item.status ? onClickUnpublish : onClickPublish}
        />
      </TableCell>
    </>
  );
}
